// Grammar Units 7 Data - Unit 7 (Subject-Verb Agreement Advanced)
// Based on Ch07.jsx - Subject-Verb Agreement Advanced Learning
// Organized for Supabase migration and systematic data management

export const grammarUnits7Data = [
  {
    id: 7,
    title: { traditional: '主詞動詞一致性進階', simplified: '主词动词一致性进阶', english: 'Advanced Subject-Verb Agreement' },
    subtitle: { traditional: '掌握主詞動詞一致性的進階規則', simplified: '掌握主词动词一致性的进阶规则', english: 'Master advanced rules of subject-verb agreement' },
    description: { traditional: '深入學習英文主詞動詞一致性的進階規則和複雜情況，提升語法準確性。', simplified: '深入学习英文主词动词一致性的进阶规则和复杂情况，提升语法准确性。', english: 'In-depth learning of advanced English subject-verb agreement rules and complex cases to improve grammatical accuracy.' },
    icon: '🎯',
    color: 'from-emerald-500 to-green-600',
    status: 'notStarted',
    difficulty: 'advanced',
    estimatedHours: 2.5,
    content: {
      overview: {
        title: "主詞動詞一致性進階 (Advanced Subject-Verb Agreement)",
        concept: "Advanced rules for subject-verb agreement in complex sentence structures",
        whenToUse: "In complex sentences with multiple subjects, clauses, or special constructions",
        howToUse: "Apply advanced rules to ensure grammatical correctness in sophisticated contexts",
        memoryTip: "Focus on identifying the true subject and understanding complex sentence structures"
      },
      complexSubjects: {
        title: "1. 複雜主詞 Complex Subjects",
        concept: "Complex subjects require careful analysis to determine verb agreement",
        rules: [
          {
            rule: "Subjects with Prepositional Phrases",
            concept: "Ignore prepositional phrases when determining subject-verb agreement",
            examples: [
              { en: "The book on the table is mine.", zh: "桌上的書是我的。" },
              { en: "The students in the library are studying.", zh: "圖書館裡的學生正在讀書。" }
            ],
            tip: "Cross out prepositional phrases to find the true subject"
          },
          {
            rule: "Subjects with Relative Clauses",
            concept: "The verb agrees with the antecedent of the relative pronoun",
            examples: [
              { en: "The man who lives next door is a doctor.", zh: "住在隔壁的男人是醫生。" },
              { en: "The people who work here are friendly.", zh: "在這裡工作的人都很友善。" }
            ]
          }
        ]
      },
      invertedSentences: {
        title: "2. 倒裝句 Inverted Sentences",
        concept: "In inverted sentences, the verb agrees with the subject that comes after it",
        rules: [
          {
            rule: "Questions and Inverted Constructions",
            examples: [
              { en: "Where is the nearest bank?", zh: "最近的銀行在哪裡？" },
              { en: "Here comes the bus.", zh: "公車來了。" },
              { en: "There are many students in the classroom.", zh: "教室裡有很多學生。" }
            ]
          },
          {
            rule: "Sentences Beginning with 'There'",
            concept: "In 'there' sentences, the verb agrees with the subject that follows",
            examples: [
              { en: "There is a book on the table.", zh: "桌上有一本書。" },
              { en: "There are many books on the shelf.", zh: "書架上有許多書。" }
            ]
          }
        ]
      },
      compoundSubjects: {
        title: "3. 複合主詞進階 Advanced Compound Subjects",
        concept: "Advanced rules for compound subjects with various conjunctions",
        rules: [
          {
            rule: "Subjects Joined by 'and'",
            concept: "Usually takes plural verb unless subjects refer to the same person/thing",
            examples: [
              { en: "Bread and butter are my favorite foods.", zh: "麵包和奶油是我最愛的食物。" },
              { en: "Fish and chips is a popular dish.", zh: "炸魚薯條是一道受歡迎的菜。" }
            ]
          },
          {
            rule: "Subjects Joined by 'or'/'nor'",
            concept: "Verb agrees with the subject closer to it",
            examples: [
              { en: "Neither the teacher nor the students are ready.", zh: "老師和學生都沒準備好。" },
              { en: "Either the cat or the dog is making that noise.", zh: "不是貓就是狗在發出那個聲音。" }
            ]
          },
          {
            rule: "Subjects with 'as well as', 'along with', 'together with'",
            concept: "Verb agrees with the first subject only",
            examples: [
              { en: "The teacher, as well as the students, is excited.", zh: "老師和學生都很興奮。" },
              { en: "The book, along with the notes, is on the desk.", zh: "書和筆記都在桌上。" }
            ]
          }
        ]
      },
      specialCases: {
        title: "4. 特殊情況進階 Advanced Special Cases",
        rules: [
          {
            rule: "Fractions and Percentages",
            concept: "Agreement depends on the noun they refer to",
            examples: [
              { en: "Two-thirds of the students are absent.", zh: "三分之二的學生缺席。" },
              { en: "Fifty percent of the work is done.", zh: "百分之五十的工作完成了。" },
              { en: "One-third of the population lives in cities.", zh: "三分之一的人口住在城市。" }
            ]
          },
          {
            rule: "Amounts and Measurements",
            concept: "Usually take singular verbs",
            examples: [
              { en: "Ten dollars is not enough.", zh: "十塊錢不夠。" },
              { en: "Five miles is a long distance to walk.", zh: "五英里是很長的步行距離。" },
              { en: "Twenty years is a long time.", zh: "二十年是很長的時間。" }
            ]
          },
          {
            rule: "Titles and Names",
            concept: "Take singular verbs even if they contain plural words",
            examples: [
              { en: "The Three Musketeers is a classic novel.", zh: "《三劍客》是一部經典小說。" },
              { en: "The United States is a large country.", zh: "美國是一個大國。" }
            ]
          }
        ]
      },
      advancedPatterns: {
        title: "5. 進階句型 Advanced Patterns",
        rules: [
          {
            rule: "Gerunds and Infinitives as Subjects",
            concept: "Always take singular verbs",
            examples: [
              { en: "Swimming is good exercise.", zh: "游泳是很好的運動。" },
              { en: "To learn English takes time.", zh: "學英文需要時間。" }
            ]
          },
          {
            rule: "Noun Clauses as Subjects",
            concept: "Take singular verbs",
            examples: [
              { en: "What he said is true.", zh: "他說的是真的。" },
              { en: "Where she went remains a mystery.", zh: "她去哪裡仍然是個謎。" }
            ]
          },
          {
            rule: "Indefinite Pronouns",
            concept: "Most take singular verbs, but some can be singular or plural",
            examples: [
              { en: "Everyone is here.", zh: "每個人都在這裡。" },
              { en: "Some of the students are late.", zh: "有些學生遲到了。" },
              { en: "Some of the cake is gone.", zh: "有些蛋糕不見了。" }
            ]
          }
        ]
      },
      practiceQuestions: [
        {
          question: "Choose: 'The book, along with the notes, ___ on the desk.'",
          options: ["is", "are", "was", "were"],
          correct: 0,
          explanation: "With 'along with', the verb agrees with the first subject 'book'."
        },
        {
          question: "Complete: 'There ___ many students in the library.'",
          options: ["is", "are", "was", "were"],
          correct: 1,
          explanation: "In 'there' sentences, the verb agrees with the subject 'students'."
        },
        {
          question: "Which is correct? 'Two-thirds of the work ___ completed.'",
          options: ["is", "are", "was", "were"],
          correct: 0,
          explanation: "Fractions take singular verbs when referring to uncountable nouns like 'work'."
        },
        {
          question: "Choose: 'What he said ___ true.'",
          options: ["is", "are", "was", "were"],
          correct: 0,
          explanation: "Noun clauses as subjects take singular verbs."
        },
        {
          question: "Complete: 'Neither the teacher nor the students ___ ready.'",
          options: ["is", "are", "was", "were"],
          correct: 1,
          explanation: "With 'or'/'nor', the verb agrees with the closer subject 'students'."
        }
      ],
      memoryTips: {
        title: "記憶技巧 Memory Tips",
        tips: [
          {
            category: "複雜主詞",
            content: "劃掉介系詞片語，找到真正的主詞"
          },
          {
            category: "倒裝句",
            content: "動詞與後面的主詞一致"
          },
          {
            category: "複合主詞",
            content: "and = 複數，or/nor = 看較近的主詞，as well as = 看第一個主詞"
          },
          {
            category: "分數和百分比",
            content: "看指涉的名詞決定單複數"
          },
          {
            category: "動名詞/不定詞",
            content: "當主詞時，永遠用單數動詞"
          },
          {
            category: "名詞子句",
            content: "當主詞時，用單數動詞"
          }
        ]
      }
    }
  }
];

// Export individual units for specific access
export const getUnitById = (unitId) => {
  return grammarUnits7Data.find(unit => unit.id === unitId);
};

export const getUnitsByDifficulty = (difficulty) => {
  return grammarUnits7Data.filter(unit => unit.difficulty === difficulty);
};

export const getCompletedUnits = () => {
  return grammarUnits7Data.filter(unit => unit.status === 'completed');
};

// Default export
export default grammarUnits7Data;
